import {Chart} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ChartData,
  ChartTypeRegistry,
  registerables,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { LinearProgress, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {TklStat} from '../model/domain/TklStat';
import {useMemo} from 'react';
import {secondsToView} from '../util/secondsToView';

const useStyles = makeStyles((theme: Theme) => ({
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    margin: 10,
  },
  chartContainerShort: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: 10,
    marginBottom: 10,
    maxWidth: '500px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  chartDetailsContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: 70,
  },
  stateString: {
    fontFamily: 'Barlow Semi Condensed',
    fontWeight: 700,
    fontStyle: 'italic',
    fontSize: '40px',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    marginTop: '0px',
  },
  colorGreen: {
    color: '#26c900',
  },
  colorRed: {
    color: '#ff0000',
  },
  colorBlue: {
    color: '#00d3ff',
  },
  statText: {
    fontFamily: 'Roboto Condensed',
    fontWeight: 400,
    fontSize: '27px',
    color: theme.palette.primary.main,
    marginTop: '0px',
    textAlign: 'center',
  },
  overallPerformanceCardGreen: {
    backgroundColor: '#26c900',
  },
  overallPerformanceCardRed: {
    backgroundColor: '#ff0000',
  },
  overallPerformanceCardGray: {
    backgroundColor: '#cccccc',
  },
  overallPerformanceCard: {
    padding: '3rem',
    borderRadius: '10px',
    marginBottom: '3rem',
    textAlign: 'left',
  },
  overallPerformanceBasicText: {
    fontFamily: 'RobotoCondensed-Regular',
    fontSize: '22px',
    textAlign: 'left',
    padding: '0px',
    margin: '0px',
  },
  overallPerformanceMiddleText: {
    fontFamily: 'BarlowSemiCondensed-Bold',
    fontSize: '22px',
    textAlign: 'left',
    marginBottom: '1rem',
  },
  overallPerformanceHeaderText: {
    textAlign: 'left',
    fontFamily: 'BarlowSemiCondensed-Bold',
    fontSize: '40px',
    margin: '0px',
  },
  overallPerformanceCardContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '10px',
  },
  overallPerformanceHead: {
    textAlign: 'center',
    fontFamily: 'BarlowSemiCondensed-Bold',
    fontSize: '40px',
    marginBottom: '1rem',
  },
  progressContainer: {
    marginTop: '10%',
    padding: 30,
    width: '100%',
  },
}));

interface LegacyPerformanceProps {
  stat?: TklStat;
  inProgress: boolean;
}

export const LegacyPerformance: React.FC<LegacyPerformanceProps> = ({
  stat,
  inProgress,
}) => {
  const classes = useStyles();

  if (inProgress === true) {
    return (
      <div className={classes.progressContainer}>
        <LinearProgress />
      </div>
    );
  } else if (!stat) {
    return (
      <div>
        <h5 className={classes.overallPerformanceHeaderText}>
          No User Statistic Found
        </h5>
      </div>
    );
  } else {
    ChartJS.register(...registerables);
    ChartJS.register(ChartDataLabels);

    const options = {
      layout: {
        padding: {
          top: 40,
          bottom: 40,
        },
      },
      maintainAspectRatio: false,
      responsive: true,
      // barThickness: 40,
      barPercentage: 0.05,
      categoryPercentage: 1,
      minBarLength: 2,
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
        datalabels: {
          anchor: 'start' as const,
          align: 'start' as const,
        },
      },
      scales: {
        x: {
          grid: {
            color: 'transparent',
            display: true,
            drawBorder: false,
            zeroLineColor: '#ccc',
            zeroLineWidth: 1,
          },
          ticks: {
            display: false,
          },
        },
        y: {
          min: -100,
          max: 100,
          ticks: {
            display: false,
          },
          grid: {
            display: false,
            drawBorder: false,
          },
        },
      },
      title: {
        display: true,
        text: 'Overall performance',
      },
    };

    const personalScoreBiggerOrEqual =
      Math.floor(stat.myTkl) >= Math.floor(stat.myTeamTkl);

    const biggestTopScore = stat.topUsersTkl.reduce((acc, item) => {
      if (acc < item[1]) return item[1];
      return acc;
    }, 0);

    const emptyLine = {
      type: 'line',
      label: 'Line',
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      backgroundColor: 'rgba(0, 0, 0, 1)',
      borderColor: 'rgba(0, 0, 0, 1)',
      borderWidth: 1,
      pointRadius: 0,
      datalabels: {
        display: false,
      },
    };

    const datalabelsFont = [
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
        weight: 'bold',
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
      {
        size: 12,
      },
    ];

    const personalScorePosition = personalScoreBiggerOrEqual ? 7 : 3;
    const bestScorePosition = personalScoreBiggerOrEqual ? 9 : 7;

    const getDataLabelsText = (value: number[], context: any) => {
      if (value === null) return;
      if (context.dataIndex === 5) return ['Team Avg ', value[1] + '%'];
      if (context.dataIndex === bestScorePosition) {
        const plusMinusPercent =
          Math.floor(biggestTopScore) - Math.floor(stat.myTeamTkl);
        let sign = '';
        if (plusMinusPercent >= 0) sign = '+';
        const differenceValue = sign + plusMinusPercent.toFixed();
        return stat.myTkl === biggestTopScore
          ? ['You Are', 'The Top Score', biggestTopScore + '%']
          : ['Top Score', biggestTopScore + '%'];
      }
      if (context.dataIndex === personalScorePosition) {
        const plusMinusPercent =
          Math.floor(value[1]) - Math.floor(stat.myTeamTkl);
        let sign = '';
        if (plusMinusPercent >= 0) sign = '+';
        const differenceValue = sign + plusMinusPercent.toFixed();
        return ['You', value[1] + '%'];
      }
    };

    const bar = personalScoreBiggerOrEqual
      ? {
          type: 'bar' as const,
          label: 'Me',
          data: [
            null,
            null,
            null,
            null,
            null,
            [-stat.myTeamTkl, stat.myTeamTkl],
            null,
            stat.myTkl === biggestTopScore ? null : [-stat.myTkl, stat.myTkl],
            null,
            [-biggestTopScore, biggestTopScore],
            null,
          ],
          backgroundColor: [
            '#000',
            '#000',
            '#000',
            '#000',
            '#000',
            '#000',
            '#000',
            '#26c900',
            '#000',
            '#00d3ff',
            '#000',
          ],
          datalabels: {
            font: datalabelsFont,
            formatter: getDataLabelsText,
            textAlign: 'center',
          },
        }
      : {
          type: 'bar' as const,
          label: 'Me',
          data: [
            null,
            null,
            null,
            [-stat.myTkl, stat.myTkl],
            null,
            [-stat.myTeamTkl, stat.myTeamTkl],
            null,
            [-biggestTopScore, biggestTopScore],
            null,
            null,
            null,
          ],
          backgroundColor: [
            '#000',
            '#000',
            '#000',
            '#FF0000',
            '#000',
            '#000',
            '#000',
            '#00d3ff',
            '#000',
            '#000',
            '#000',
          ],
          datalabels: {
            font: datalabelsFont,
            formatter: getDataLabelsText,
            textAlign: 'center',
          },
        };

    const data = {
      labels: ['', '', '', '', '', '', '', '', '', '', ''],
      datasets: [emptyLine, bar],
    } as ChartData<keyof ChartTypeRegistry, number[] | null, string>;
    let comparisonString = (() => {
      const roundedScore = Math.floor(stat.myTkl);
      const roundedTeamScore = Math.floor(stat.myTeamTkl);
      switch (true) {
        case stat.myTkl === biggestTopScore: {
          return 'LEADING';
        }
        case roundedScore > roundedTeamScore: {
          return 'AHEAD OF';
        }
        case roundedScore < roundedTeamScore: {
          return 'BEHIND';
        }
        default:
          return 'RIGHT WITH';
      }
    })();
    let comparisonStringStyle;
    if (stat.myTkl === biggestTopScore)
      comparisonStringStyle = classes.colorBlue;
    else if (personalScoreBiggerOrEqual)
      comparisonStringStyle = classes.colorGreen;
    else comparisonStringStyle = classes.colorRed;

    return (
      <div>
        <h1 className={classes.overallPerformanceHead}>Top 3 Users:</h1>
        <div className={classes.overallPerformanceCardContainer}>
          {[...stat.topUsersTkl]
            .sort((a, b) => b[1] - a[1])
            .map((user, index) => {
              const [username] = user;
              return (
                <p
                  key={index.toString()}
                  className={classes.overallPerformanceHeaderText}>
                  {`#${index + 1}: ${username}`}
                </p>
              );
            })}
        </div>
        <div
          className={
            stat.myTkl === biggestTopScore
              ? classes.chartContainerShort
              : classes.chartContainer
          }>
          <Chart
            type="bar"
            options={options}
            data={data}
            height={300}
            width={500}
          />
        </div>
        <div className={classes.chartDetailsContainer}>
          <p className={classes.stateString}>
            YOU ARE CURRENTLY{' '}
            <span className={comparisonStringStyle}>{comparisonString}</span>{' '}
            YOUR TEAMMATES
          </p>
          {/* <p className={classes.statText}>
            Total Cards Reviewed: {stat.totalCards}
          </p>
          <p className={classes.statText}>
            Average Time Per Session: {secondsToView(stat.avgSessionTime)}
          </p>
          <p className={classes.statText}>
            Average Cards Per Session: {stat.avgSessionCards}
          </p>
          {stat.mostDifficultCards && stat.mostDifficultCards.length > 0 && (
            <p className={classes.statText}>
              Most Difficult 3 Cards (historically):
            </p>
          )}
          {stat.mostDifficultCards?.map((mdc) => {
            return <p className={classes.statText}>{mdc}</p>;
          })} */}
        </div>
      </div>
    );
  }
};
